import { useState } from "react";

import usePartAiTitle from "../data/usePartAiTitle";
import { IPart } from "../models/Part";
import runWithConcurrency from "../utils/runWithConcurrency";

// Each title is one OpenAI round trip on the API, so keep a handful in flight rather than
// firing the whole selection at once.
const AI_TITLE_CONCURRENCY = 4;

export interface IGeneratedAiTitle {
  part: IPart;
  title: string;
}

export interface IBulkAiTitlesResult {
  generated: IGeneratedAiTitle[];
  failed: number;
}

/**
 * Generates AI eBay titles for many parts, reporting progress as they come back.
 * Only generates - the caller decides how to persist (stage on the offer vs queue an eBay revise).
 */
export default function useBulkAiTitles() {
  const { generateAiTitle } = usePartAiTitle();
  const [progress, setProgress] = useState<{ completed: number; total: number } | null>(null);

  const generateTitles = async (parts: IPart[]): Promise<IBulkAiTitlesResult> => {
    setProgress({ completed: 0, total: parts.length });

    try {
      const results = await runWithConcurrency(
        parts,
        AI_TITLE_CONCURRENCY,
        async (part) => {
          const response = await generateAiTitle.mutateAsync(part.id);
          if (!response.success || !response.data?.title) {
            throw new Error(response.message ?? "Unable to generate AI title");
          }
          return response.data.title;
        },
        (completed, total) => setProgress({ completed, total })
      );

      const generated: IGeneratedAiTitle[] = [];
      results.forEach((result) => {
        if (result.ok) generated.push({ part: result.item, title: result.value });
      });

      return { generated, failed: results.length - generated.length };
    } finally {
      setProgress(null);
    }
  };

  const label = progress ? `Generating AI Titles (${progress.completed}/${progress.total})` : "Generate AI Titles";

  return {
    generateTitles,
    isGenerating: progress !== null,
    label,
  };
}
